import { createHmac } from "crypto";
import { STATE_TOKEN_SECRET_KEY } from "@shared/env";

export class StateTokenService {
  private static SECRET_KEY =
    STATE_TOKEN_SECRET_KEY || "b8f0e4c1a2d3e6f58b9a1c2e3f4a5b9e5f6b7c8d9e0f1";

  /**
   * Creates a state token using a unique identifier and HMAC hash.
   * The state token is used to maintain state between the request and callback.
   *
   * @returns {string} The generated state token.
   */
  public static create(): string {
    const uniqueId = this.generateUniqueId(); // Generate a unique ID
    const hmac = createHmac("sha256", this.SECRET_KEY); // Create an HMAC instance with the secret key
    hmac.update(uniqueId); // Hash the unique ID
    const hash = hmac.digest("hex"); // Get the resulting HMAC hash
    return `${uniqueId}:${hash}`; // Combine the unique ID and hash to form the state token
  }

  /**
   * Verifies the provided state token against the expected HMAC hash.
   * The state token is split into the unique ID and the hash,
   * and the hash is recomputed to check its validity.
   *
   * @param {string} stateToken - The state token to verify.
   * @returns {boolean} True if the token is valid, false otherwise.
   */
  public static verify(stateToken: string): boolean {
    const [uuid, hash] = stateToken.split(":");
    const hmac = createHmac("sha256", this.SECRET_KEY);
    hmac.update(uuid);
    const expectedHash = hmac.digest("hex");
    return expectedHash === hash;
  }

  /**
   * Generates a unique identifier by combining the current timestamp
   * and a random string. This ensures that each identifier is unique.
   *
   * @returns {string} The generated unique identifier.
   */
  private static generateUniqueId(): string {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 11);
    return `${timestamp}-${randomString}`;
  }
}
